//Users/kacey/BoxyQR_Website/BoxyQR/boxyQR_website/client/src/pages/ManageAccount.js
import React, { useState, useEffect } from 'react';
import { useUser, useSession } from '@clerk/clerk-react';
import DeleteConfirmModal from '../components/modals/DeleteConfirmModal';
import { handleDeleteAccount } from '../utils/accountDeletion';



function ManageAccount() {
  const { user } = useUser();
  const { session } = useSession();

  const [loading, setLoading] = useState(true);
  const [account, setAccount] = useState(null);
    const [processingPayment, setProcessingPayment] = useState(false);
  
  const [accountId, setAccountId] = useState(null);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [formData, setFormData] = useState({
    companyName: '',
    phoneNumber: '',
    address: ''
  });

  // Fetch account data
  useEffect(() => {
    const fetchAccount = async () => {
      try {
        if (!user) {
          setError('You must be logged in to view this page');
          setLoading(false);
          return;
        }
        
        const userResponse = await fetch(`/api/users/by-clerk/${user.id}`, {
            headers: {
              'Authorization': `Bearer ${session?.getToken()}`
            }
          });

        
        if (!userResponse.ok) {
          setError('Unable to fetch user details. Please try again later.');
          setLoading(false);
          return;
        }
        
        const userData = await userResponse.json();
        
        if (!userData || !userData.account_id) {
          setError('Your account is not properly set up. Please contact support.');
          setLoading(false);
          return;
        }
        
        // Store the account_id
        const userAccountId = userData.account_id;
        setAccountId(userAccountId);
        
        // Step 2: Fetch account using account_id
        const accountResponse = await fetch(`/api/account/${userAccountId}`);
        
        if (!accountResponse.ok) {
          setError('Failed to fetch account details. Please try again later.');
          setLoading(false);
          return;
        }
        
        const accountData = await accountResponse.json();
        setAccount(accountData);
        
        // Populate form data
        setFormData({
          companyName: accountData.companyName || '',
          phoneNumber: accountData.phoneNumber || '',
          address: accountData.address || ''
        });
      } catch (err) {
        console.error('Error fetching account:', err);
        setError('An error occurred. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchAccount();
  }, [user, session]);

  // Handle form changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSuccessMessage('');
    setError('');
    
    try {
      const response = await fetch(`/api/account/${accountId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || 'Failed to update account');
      }
      
      setSuccessMessage('Account information updated successfully');
    } catch (err) {
      console.error('Error updating account:', err);
      setError(err.message || 'Failed to update account');
    }
  };


  // Delete account confirmation modal

  if (loading) {
    return <div className="text-center py-10">Loading...</div>;
  }

  if (error) {
    return (
      <div className="bg-red-100 border-l-4 border-red-500 p-4 mb-6 max-w-4xl mx-auto mt-8">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-red-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <p className="text-sm text-red-700">{error}</p>
            <p className="text-sm text-red-700 mt-1">
              <button onClick={() => window.location.reload()} className="underline">
                Try refreshing the page
              </button>
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold mb-6">Manage Account</h1>
      
      {successMessage && (
        <div className="bg-green-100 border-l-4 border-green-500 p-4 mb-6">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-green-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-green-700">{successMessage}</p>
            </div>
          </div>
        </div>
      )}
      
      {/* Account Information Section */}
      <div className="bg-white p-6 rounded-lg shadow-md mb-8">
        <h2 className="text-xl font-semibold mb-4">Account Information</h2>
        
        <div className="mb-4">
          <p className="text-gray-600 mb-1">Email Address:</p>
          <p className="font-medium">{account?.email || user?.primaryEmailAddress?.emailAddress || 'Not available'}</p>
        </div>
        
        <div className="mb-4">
          <p className="text-gray-600 mb-1">Current Plan:</p>
          <p className="font-medium capitalize">{account?.plan?.replace('_', ' ')}</p>
        </div>
        
        <div className="mb-4">
          <p className="text-gray-600 mb-1">Account Created:</p>
          <p className="font-medium">{new Date(account?.createdAt).toLocaleDateString()}</p>
        </div>
        
        {account?.subscription && (
          <>
            <div className="mb-4">
              <p className="text-gray-600 mb-1">Subscription Status:</p>
              <p className="font-medium capitalize">{account.subscription.status}</p>
            </div>
            
            {account.subscription.currentPeriodEnd && (
              <div className="mb-4">
                <p className="text-gray-600 mb-1">Next Billing Date:</p>
                <p className="font-medium">{new Date(account.subscription.currentPeriodEnd).toLocaleDateString()}</p>
              </div>
            )}
          </>
        )}
        
        <div className="mt-6">
          <a href="/upgrade" className="text-blue-600 hover:text-blue-800 font-semibold">
            Manage Subscription Plan →
          </a>
        </div>
      </div>
      
      {/* Company Information Form */}
      {/* <div className="bg-white p-6 rounded-lg shadow-md mb-8"> */}
        {/* <h2 className="text-xl font-semibold mb-4">Company Information</h2> */}
        
        {/* <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="companyName">
              Company Name
            </label>
            <input
              className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
              id="companyName"
              name="companyName"
              type="text"
              value={formData.companyName}
              onChange={handleChange}
              placeholder="Your company name"
            />
          </div>
          
          <div className="mb-4">
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="phoneNumber">
              Phone Number
            </label>
            <input
              className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
              id="phoneNumber"
              name="phoneNumber"
              type="text"
              value={formData.phoneNumber}
              onChange={handleChange}
              placeholder="Your phone number"
            />
          </div>
          
          <div className="mb-6">
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="address">
              Business Address
            </label>
            <textarea
              className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
              id="address"
              name="address"
              value={formData.address}
              onChange={handleChange}
              placeholder="Your business address"
              rows="3"
            ></textarea>
          </div>
          
          <div className="flex items-center justify-end">
            <button
              className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
              type="submit"
            >
              Save Changes
            </button>
          </div>
        </form> */}
      {/* </div> */}
      
      {/* Account Security Section */}
      <div className="bg-white p-6 rounded-lg shadow-md mb-8">
        <h2 className="text-xl font-semibold mb-4">Security</h2>
        
        <div className="mb-6">
          <p className="text-gray-600 mb-3">Change your password or update your security settings.</p>
          <button
            onClick={() => {
              // Redirect to Clerk's user profile page or handle it based on your auth system
              if (window.Clerk) {
                window.Clerk.openUserProfile();
              }
            }}
            className="bg-gray-600 hover:bg-gray-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
          >
            Manage Security Settings
          </button>
        </div>
      </div>
      
      {/* Danger Zone */}
      <div className="bg-white p-6 rounded-lg shadow-md mb-8 border border-red-200">
        <h2 className="text-xl font-semibold mb-4 text-red-600">Danger Zone</h2>
        
        <div className="mb-4">
          <p className="text-gray-600 mb-3">
            Permanently delete your account and all associated data. This action cannot be undone.
          </p>
          <button
  onClick={() => setShowDeleteConfirm(true)} // This is the corrected line
  className="px-4 py-2 bg-red-600 hover:bg-red-700 font-semibold text-white rounded shadow-sm"
>
  Delete Account
</button>
        </div>
      </div>
      
      {/* Delete Account Confirmation Modal */}
      <DeleteConfirmModal
  showDeleteConfirm={showDeleteConfirm}
  setShowDeleteConfirm={setShowDeleteConfirm}
  accountId={accountId}
  user={user}
  setError={setError}
  processingPayment={processingPayment}
  setProcessingPayment={setProcessingPayment}
  
/>
    </div>
  );
}

export default ManageAccount;